import { Check, ChevronDown, Plus, X } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { cn } from "@/lib/utils";

// ── Types ────────────────────────────────────────────────────────────────────

type Primitive = string | number;

interface MultiSearchableSelectProps<T extends Record<string, any>> {
  /** The list of options to display */
  options: T[] | Record<string, Primitive> | null | undefined;
  /** Key used to display the option label */
  labelKey?: string;
  /** Key used as the unique value identifier */
  valueKey?: string;
  /** Currently selected items (controlled) */
  selected?: T[] | null | undefined;
  /** Callback when selection changes */
  onChange: (selected: T[]) => void;
  /** Enable the search/filter input inside the dropdown */
  searchable?: boolean;
  /** Allow creating new items by typing a custom value */
  allowCreate?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

// ── Component ────────────────────────────────────────────────────────────────

export function MultiSearchableSelect<T extends Record<string, any>>({
  options,
  labelKey = "name",
  valueKey = "id",
  selected,
  onChange,
  searchable = true,
  allowCreate = true,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  disabled = false,
  className,
}: MultiSearchableSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownPos, setDropdownPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getLabel = (item: T): string =>
    String(getNestedValue(item, labelKey) ?? "");
  const getValue = (item: T): Primitive => {
    const val = getNestedValue(item, valueKey);
    return typeof val === "number" ? val : String(val ?? "");
  };

  const selectedArray: T[] = useMemo(() => {
    if (Array.isArray(selected)) return selected;
    return [];
  }, [selected]);

  // Normalize options
  const normalizedOptions = useMemo(() => {
    if (!options) return [];

    if (Array.isArray(options)) {
      if (options.length === 0) return [];
      if (typeof options[0] === "string" || typeof options[0] === "number") {
        return (options as unknown as Primitive[]).map((val) => ({
          item: { [labelKey]: String(val), [valueKey]: val } as unknown as T,
          label: String(val),
          val,
        }));
      }
      return options.map((item) => ({
        item,
        label: getLabel(item),
        val: getValue(item),
      }));
    }

    return Object.entries(options).map(([label, val]) => ({
      item: { [labelKey]: label, [valueKey]: val } as unknown as T,
      label,
      val,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, labelKey, valueKey]);

  // Selected values mapped to normalized rows
  const selectedValues = useMemo(() => {
    const set = new Set<string>(
      selectedArray.map((item) => String(getValue(item))),
    );
    // Include items from normalized options, plus any selected items not in options
    const fromOptions = normalizedOptions.filter((opt) =>
      set.has(String(opt.val)),
    );
    const optionVals = new Set(fromOptions.map((o) => String(o.val)));
    const missing = selectedArray
      .filter((item) => !optionVals.has(String(getValue(item))))
      .map((item) => ({ item, label: getLabel(item), val: getValue(item) }));
    return [...fromOptions, ...missing].filter(
      (o) => o.label.trim().length > 0,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArray, normalizedOptions]);

  // Filter by search
  const filteredOptions = useMemo(() => {
    if (!search.trim()) return normalizedOptions;
    const q = search.toLowerCase();
    return normalizedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(q),
    );
  }, [normalizedOptions, search]);

  // Check if search text matches an existing option
  const searchMatchesExisting = useMemo(() => {
    if (!search.trim()) return true;
    return normalizedOptions.some(
      (opt) => opt.label.toLowerCase() === search.trim().toLowerCase(),
    );
  }, [search, normalizedOptions]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Position & focus on open
  useEffect(() => {
    if (open) {
      const el = triggerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = Math.min(
          filteredOptions.length * 40 + (searchable ? 44 : 0),
          240,
        );
        const openUpward =
          spaceBelow < dropdownHeight && rect.top > dropdownHeight;
        setDropdownPos({
          top: openUpward ? undefined : rect.bottom + 4,
          bottom: openUpward ? window.innerHeight - rect.top + 4 : undefined,
          left: Math.max(
            8,
            Math.min(rect.left, window.innerWidth - rect.width - 8),
          ),
          width: rect.width,
        });
      }
      if (searchable) {
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  }, [open, filteredOptions.length, searchable]);

  const commit = (arr: T[]) => {
    const seen = new Set<string>();
    const dedup = arr.filter((item) => {
      const v = String(getValue(item));
      if (seen.has(v)) return false;
      seen.add(v);
      return true;
    });
    onChange(dedup);
  };

  const toggleSelection = (option: { item: T; val: Primitive }) => {
    commit([option.item]);
    setOpen(false);
    setSearch("");
  };

  const removeChip = (val: Primitive) => {
    commit(
      selectedArray.filter((item) => String(getValue(item)) !== String(val)),
    );
  };

  const addCustomItem = () => {
    const name = search.trim();
    if (!name) return;
    if (searchMatchesExisting) return;

    const newItem = {
      [labelKey]: name,
      [valueKey]: `${name}_${Date.now()}`,
    } as unknown as T;

    commit([...selectedArray, newItem]);
    setSearch("");
    inputRef.current?.focus();
  };

  return (
    <div className={cn("relative w-full", className)} ref={containerRef}>
      {/* Trigger area with chips */}
      <div
        ref={triggerRef}
        onClick={() => !disabled && setOpen(true)}
        className={cn(
          "flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-[13px]",
          "focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        {selectedValues.map((option) => (
          <span
            key={String(option.val)}
            className="inline-flex items-center gap-1 rounded-sm bg-primary/10 px-2 py-0.5 text-[13px] font-medium text-primary"
          >
            {option.label}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeChip(option.val);
              }}
              className="text-primary hover:text-destructive"
            >
              <X size={12} />
            </button>
          </span>
        ))}

        {selectedValues.length === 0 && !open && (
          <span className="text-muted-foreground">{placeholder}</span>
        )}

        {open && searchable && (
          <input
            ref={inputRef}
            type="text"
            className="min-w-[50px] flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
            placeholder={
              selectedValues.length === 0 ? placeholder : searchPlaceholder
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (allowCreate && !searchMatchesExisting) {
                  addCustomItem();
                }
              }
              if (
                e.key === "Backspace" &&
                !search &&
                selectedValues.length > 0
              ) {
                removeChip(selectedValues[selectedValues.length - 1].val);
              }
            }}
            disabled={disabled}
          />
        )}

        {!open && (
          <ChevronDown
            size={16}
            className="ml-auto shrink-0 text-muted-foreground"
          />
        )}
      </div>

      {/* Dropdown panel */}
      {open &&
        dropdownPos &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: dropdownPos.top,
              bottom: dropdownPos.bottom,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 1000000,
              pointerEvents: "auto",
            }}
            className="rounded-md border bg-popover shadow-md"
          >
            {/* Options list */}
            <div className="max-h-40 overflow-y-auto py-1">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isSelected = selectedValues.some(
                    (sel) => String(sel.val) === String(option.val),
                  );
                  return (
                    <button
                      key={String(option.val)}
                      type="button"
                      onClick={() => toggleSelection(option)}
                      className={cn(
                        "flex w-full items-center justify-between px-3 py-2 text-left text-[13px] hover:bg-accent cursor-pointer",
                        isSelected && "bg-accent/50 font-normal",
                      )}
                    >
                      <span className="truncate">{option.label}</span>
                      {isSelected && (
                        <Check
                          size={16}
                          className="ml-2 shrink-0 text-primary"
                        />
                      )}
                    </button>
                  );
                })
              ) : allowCreate && search.trim() ? (
                <button
                  type="button"
                  onClick={addCustomItem}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-primary hover:bg-accent cursor-pointer"
                >
                  <Plus size={16} />
                  Add &ldquo;{search}&rdquo;
                </button>
              ) : (
                <p className="px-3 py-4 text-center text-[13px] text-muted-foreground">
                  No options found.
                </p>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
